export type PanBidderType =
  | "individual"
  | "proprietors"
  | "llpfirm"
  | "partnershipfirm"
  | "company"
  | "trust"
  | "huf"
  | "aop"
  | "boi"
  | "govt"
  | "local"
  | "ajp";

export type BidderTypeOption = {
  label: string;
  value: PanBidderType;
};

export type AddressProofOption = {
  label: string;
  value: string;
};

export type NonIndividualBidderTypeValue =
  | Exclude<PanBidderType, "individual">
  | "llp"
  | "partnership";

export const ALL_BIDDER_TYPE_OPTIONS: BidderTypeOption[] = [
  { label: "Individual", value: "individual" },
  { label: "Proprietors", value: "proprietors" },
  { label: "Limited Liability Partnership (LLP)", value: "llpfirm" },
  { label: "Partnership Firm", value: "partnershipfirm" },
  { label: "Company", value: "company" },
  { label: "Trust", value: "trust" },
  { label: "Hindu Undivided Family", value: "huf" },
  { label: "Association of Persons", value: "aop" },
  { label: "Body of Individuals", value: "boi" },
  { label: "Government Agency", value: "govt" },
  { label: "Local Authority", value: "local" },
  { label: "Artificial Juridical Person", value: "ajp" },
];

export const NON_INDIVIDUAL_BIDDER_TYPES: NonIndividualBidderTypeValue[] = [
  "company",
  "proprietors",
  "partnership",
  "llp",
  "llpfirm",
  "partnershipfirm",
  "trust",
  "huf",
  "aop",
  "boi",
  "govt",
  "local",
  "ajp",
];

const ADDRESS_PROOF_OPTIONS_BY_VALUE: Record<string, AddressProofOption> = {
  utility_bill_recent: {
    label: "Utility Bill (<= 2 months)",
    value: "utility_bill_recent",
  },
  utility_bill: {
    label: "Utility Bill",
    value: "utility_bill",
  },
  utility_bill_business_or_proprietor_name: {
    label: "Utility Bill (Business / Proprietor Name)",
    value: "utility_bill_business_or_proprietor_name",
  },
  utility_bill_huf_or_karta_name: {
    label: "Utility Bill (HUF / Karta Name)",
    value: "utility_bill_huf_or_karta_name",
  },
  registered_lease_agreement: {
    label: "Registered Lease Agreement",
    value: "registered_lease_agreement",
  },
  lease_agreement: {
    label: "Lease Agreement",
    value: "lease_agreement",
  },
  lease_agreement_india_office: {
    label: "Lease Agreement (India Office)",
    value: "lease_agreement_india_office",
  },
  property_tax_receipt: {
    label: "Property Tax Receipt",
    value: "property_tax_receipt",
  },
  roc_form_inc_22_latest: {
    label: "ROC Form INC-22 (Latest)",
    value: "roc_form_inc_22_latest",
  },
  roc_form_llp_15_3: {
    label: "ROC Form (LLP Form 15/3)",
    value: "roc_form_llp_15_3",
  },
  udyam_certificate: {
    label: "Udyam Certificate",
    value: "udyam_certificate",
  },
  shops_establishment_certificate: {
    label: "Shops & Establishment Certificate",
    value: "shops_establishment_certificate",
  },
  trade_license: {
    label: "Trade License",
    value: "trade_license",
  },
  bank_statement_business_address: {
    label: "Bank Statement (Business Address)",
    value: "bank_statement_business_address",
  },
  bank_statement: {
    label: "Bank Statement",
    value: "bank_statement",
  },
  registration_certificate_with_address: {
    label: "Registration Certificate (Address Mentioned)",
    value: "registration_certificate_with_address",
  },
  rbi_filings_if_any: {
    label: "RBI Filings (if any)",
    value: "rbi_filings_if_any",
  },
};

const DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES: string[] = [
  "utility_bill",
  "lease_agreement",
  "property_tax_receipt",
  "registration_certificate_with_address",
];

const ADDRESS_PROOF_BY_BIDDER_TYPE: Record<
  Exclude<PanBidderType, "individual">,
  string[]
> = {
  company: [
    "utility_bill_recent",
    "registered_lease_agreement",
    "property_tax_receipt",
    "roc_form_inc_22_latest",
    "udyam_certificate",
    "lease_agreement_india_office",
  ],
  llpfirm: [
    "utility_bill_recent",
    "lease_agreement",
    "property_tax_receipt",
    "roc_form_llp_15_3",
    "udyam_certificate",
  ],
  partnershipfirm: [
    "utility_bill",
    "lease_agreement",
    "property_tax_receipt",
    "shops_establishment_certificate",
    "udyam_certificate",
  ],
  proprietors: [
    "utility_bill_business_or_proprietor_name",
    "shops_establishment_certificate",
    "udyam_certificate",
    "trade_license",
    "bank_statement_business_address",
  ],
  trust: [
    "utility_bill",
    "lease_agreement",
    "property_tax_receipt",
    "registration_certificate_with_address",
  ],
  huf: ["utility_bill_huf_or_karta_name", "property_tax_receipt", "bank_statement"],
  aop: DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES,
  boi: DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES,
  govt: DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES,
  local: DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES,
  ajp: DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES,
};

// PAN 4th character -> bidder type(s)
// Example: CHSPV6814L => "P" => individual/proprietors
const PAN_CHAR_TO_SYSTEM_TYPES: Record<string, PanBidderType[]> = {
  P: ["individual", "proprietors"],
  C: ["company"],
  F: ["partnershipfirm"],
  E: ["llpfirm"],
  T: ["trust"],
  H: ["huf"],
  A: ["aop"],
  B: ["boi"],
  G: ["govt"],
  L: ["local"],
  J: ["ajp"],
};

// Digio category -> bidder type(s) used in onboarding
const DIGIO_CATEGORY_TO_SYSTEM_TYPES: Record<string, PanBidderType[]> = {
  individual: ["individual", "proprietors"],
  proprietorship: ["individual", "proprietors"],
  single_owner: ["individual", "proprietors"],
  proprietor: ["individual", "proprietors"],
  proprietors: ["individual", "proprietors"],
  company: ["company"],
  partnership: ["partnershipfirm"],
  partnership_firm: ["partnershipfirm"],
  llp: ["llpfirm"],
  limited_liability_partnership: ["llpfirm"],
  firm: ["partnershipfirm", "llpfirm"],
  trust: ["trust"],
  huf: ["huf"],
  hindu_undivided_family: ["huf"],
  aop: ["aop"],
  association_of_persons: ["aop"],
  boi: ["boi"],
  body_of_individuals: ["boi"],
  govt: ["govt"],
  government: ["govt"],
  government_agency: ["govt"],
  local: ["local"],
  local_authority: ["local"],
  ajp: ["ajp"],
  artificial_juridical_person: ["ajp"],
  artificial_judicial_person: ["ajp"],
};

export const normalizeBidderTypeForPanValidation = (
  bidderType?: string,
): PanBidderType | "" => {
  if (!bidderType) return "";
  const normalized = bidderType.toLowerCase().trim();
  if (normalized === "proprietorship" || normalized === "proprietor") {
    return "proprietors";
  }
  if (normalized === "llp") return "llpfirm";
  if (normalized === "partnership") return "partnershipfirm";
  return (normalized as PanBidderType) || "";
};

export const getBidderTypeLabel = (bidderType?: string) => {
  const normalizedType = normalizeBidderTypeForPanValidation(bidderType);
  if (!normalizedType) return "";
  return (
    ALL_BIDDER_TYPE_OPTIONS.find((item) => item.value === normalizedType)
      ?.label || ""
  );
};

export const getPanTypesByPan = (panValue: string): PanBidderType[] => {
  const panTypeKey = panValue?.trim()?.toUpperCase()?.charAt(3);
  return PAN_CHAR_TO_SYSTEM_TYPES[panTypeKey] || [];
};

export const normalizeDigioCategory = (value?: string): string =>
  value
    ?.toLowerCase()
    ?.trim()
    ?.replace(/[\s-]+/g, "_")
    ?.replace(/_+/g, "_") || "";

export const mapDigioCategoryToBidderTypes = (
  digioCategory?: string,
): PanBidderType[] => {
  const normalizedCategory = normalizeDigioCategory(digioCategory);
  return DIGIO_CATEGORY_TO_SYSTEM_TYPES[normalizedCategory] || [];
};

export const getPanTypesByDigioCategory = (
  digioCategory?: string,
): PanBidderType[] => {
  return mapDigioCategoryToBidderTypes(digioCategory);
};

export const getAddressProofOptionsByBidderType = (
  bidderType?: string,
): AddressProofOption[] => {
  const normalizedBidderType = normalizeBidderTypeForPanValidation(bidderType);
  const mappedValues =
    normalizedBidderType && normalizedBidderType !== "individual"
      ? ADDRESS_PROOF_BY_BIDDER_TYPE[normalizedBidderType]
      : DEFAULT_NON_INDIVIDUAL_ADDRESS_PROOF_VALUES;

  return mappedValues
    .map((value) => ADDRESS_PROOF_OPTIONS_BY_VALUE[value])
    .filter(Boolean);
};

